class CarouselImage extends HTMLElement {
  constructor() {
    super();

    // 创建Shadow DOM
    this.attachShadow({ mode: 'open' });

    // 获取配置参数 - 修改布尔属性的处理方式
    this.autoplay = this.hasAttribute('autoplay');
    this.loop = this.hasAttribute('loop');
    this.initialIndex = parseInt(this.getAttribute('initial-index') || '0');
    this.speed = parseInt(this.getAttribute('speed') || '500');
    this.duration = parseInt(this.getAttribute('duration') || '3000');
    this.showArrows = this.hasAttribute('arrows');
    this.showPagination = this.hasAttribute('pagination');
    this.aspectRatio = this.getAttribute('aspect-ratio');

    // 获取所有子元素（图片或链接）
    this.items = Array.from(this.children);
    this.currentIndex = this.initialIndex || 0;
    this.totalItems = this.items.length;
    this.autoplayInterval = null;
    this.isTransitioning = false; // 确保初始状态为false
  }

  connectedCallback() {
    // 如果只有一张图片，简化处理
    if (this.totalItems <= 1) {
      this.renderSingleItem();
      return;
    }

    // 创建基本HTML结构
    const container = document.createElement('div');
    container.className = 'slider-container';

    // 创建滑动轨道
    const slideTrack = document.createElement('div');
    slideTrack.className = 'slider-track';
    container.appendChild(slideTrack);

    // 添加箭头按钮
    if (this.showArrows) {
      const prevButton = document.createElement('button');
      prevButton.className = 'arrow-button prev-button';
      prevButton.innerHTML = '<svg fill="currentColor" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512"><path d="M238.475 36.465l7.071 7.07c4.686 4.686 4.686 12.284 0 16.971L50.053 256l195.493 195.494c4.686 4.686 4.686 12.284 0 16.971l-7.071 7.07c-4.686 4.686-12.284 4.686-16.97 0L21.475 264.485c-4.686-4.686-4.686-12.284 0-16.971L221.505 36.465c4.686-4.687 12.284-4.687 16.97 0z"/></svg>';
      container.appendChild(prevButton);

      const nextButton = document.createElement('button');
      nextButton.className = 'arrow-button next-button';
      nextButton.innerHTML = '<svg fill="currentColor" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512"><path d="M17.525 36.465l-7.071 7.07c-4.686 4.686-4.686 12.284 0 16.971L205.947 256 10.454 451.494c-4.686 4.686-4.686 12.284 0 16.971l7.071 7.07c4.686 4.686 12.284 4.686 16.97 0l211.051-211.05c4.686-4.686 4.686-12.284 0-16.971L34.495 36.465c-4.686-4.687-12.284-4.687-16.97 0z"/></svg>';
      container.appendChild(nextButton);
    }

    // 添加分页容器
    if (this.showPagination) {
      const pagination = document.createElement('div');
      pagination.className = 'pagination';
      container.appendChild(pagination);
    }

    // 添加内部样式
    const style = document.createElement('style');
    style.textContent = `
      :host {
        display: block;
        position: relative;
      }
      .slider-container {
        position: relative;
        overflow: hidden;
        width: 100%;
      }
      .slider-track {
        display: flex;
        height: 100%;
        backface-visibility: hidden;
        will-change: transform;
        transition-property: transform;
        transition-timing-function: ease;
      }
      .slide {
        flex-shrink: 0;
        width: 100%;
        height: auto;
      }
      img, a {
        display: block;
        width: 100%;
        height: auto;
        ${this.aspectRatio ? `aspect-ratio: ${this.aspectRatio};` : ''}
      }
      img {
        display: block;
      }
      .arrow-button {
        position: absolute;
        color: black;
        background-color: rgba(0, 0, 0, 0);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        cursor: pointer;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        top: 50%;
        transform: translateY(-50%);
      }
      .prev-button {
        left: 10px;
      }
      .next-button {
        right: 10px;
      }
      .arrow-button svg {
        height: 20px;
      }
      .pagination {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        justify-content: center;
        padding: 10px 0;
        gap: 8px;
      }
      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: transparent;
        cursor: pointer;
        border: 1px solid black;
      }
      .dot.active {
        background-color: #333;
      }
    `;

    // 先添加样式，再添加容器到DOM
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);

    // 获取DOM元素引用
    this.container = container;
    this.slideTrack = slideTrack;

    if (this.showArrows) {
      this.prevButton = this.shadowRoot.querySelector('.prev-button');
      this.nextButton = this.shadowRoot.querySelector('.next-button');
    }

    if (this.showPagination) {
      this.paginationContainer = this.shadowRoot.querySelector('.pagination');
    }

    // 创建幻灯片
    this.createSlides();

    // 创建分页指示器
    if (this.showPagination) {
      this.createPagination();
    }

    // 设置事件监听器
    this.setupEventListeners();

    // 设置过渡时间
    if (this.slideTrack) {
      this.slideTrack.style.transitionDuration = `${this.speed}ms`;
    }

    // 显示初始幻灯片
    if (this.slideTrack) {
      // 初始显示不需要动画
      const offset = this.loop ? -(this.currentIndex + 1) * 100 : -this.currentIndex * 100;
      requestAnimationFrame(() => {
        this.slideTrack.style.transition = 'none';
        this.slideTrack.style.transform = `translateX(${offset}%)`;
        requestAnimationFrame(() => {
          this.slideTrack.style.transition = `transform ${this.speed}ms ease`;
        });
      });
    }

    // 更新分页指示器
    if (this.showPagination) {
      this.updatePagination();
    }

    // 开始自动播放
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  renderSingleItem() {
    // 如果只有一张图片，简化处理
    const item = this.items[0];
    const isLink = item.tagName === 'A';

    // 提取共同样式
    const commonStyles = `
      <style>
        :host {
          display: block;
        }
        .single-item {
          width: 100%;
          overflow: hidden;
        }
        img {
          width: 100%;
          height: auto;
          display: block;
          ${this.aspectRatio ? `aspect-ratio: ${this.aspectRatio};` : ''}
        }
      </style>
    `;

    // 根据是否为链接创建不同的HTML
    let contentHTML;
    if (isLink) {
      const img = item.querySelector('img');
      contentHTML = `
        <a href="${item.getAttribute('href')}" target="${item.getAttribute('target') || '_self'}" class="single-item">
          <img src="${img.getAttribute('src')}" alt="${img.getAttribute('alt') || ''}" />
        </a>
      `;
    } else {
      contentHTML = `
        <div class="single-item">
          <img src="${item.getAttribute('src')}" alt="${item.getAttribute('alt') || ''}" />
        </div>
      `;
    }

    // 组合样式和内容
    this.shadowRoot.innerHTML = commonStyles + contentHTML;
  }

  createSlides() {
    // 如果启用了循环，添加克隆幻灯片
    if (this.loop) {
      // 克隆最后一个元素放在开头
      const lastItemClone = this.cloneItem(this.items[this.totalItems - 1]);
      this.appendSlide(lastItemClone, -1);

      // 添加原始元素
      this.items.forEach((item, index) => {
        this.appendSlide(item, index);
      });

      // 克隆第一个元素放在结尾
      const firstItemClone = this.cloneItem(this.items[0]);
      this.appendSlide(firstItemClone, this.totalItems);

      // 初始位置设为第一张实际图片（跳过克隆的最后一张）
      this.slideTrack.style.transform = `translateX(-${(this.initialIndex + 1) * 100}%)`;
    } else {
      // 不循环时直接添加原始元素
      this.items.forEach((item, index) => {
        this.appendSlide(item, index);
      });

      // 初始位置
      this.slideTrack.style.transform = `translateX(-${this.initialIndex * 100}%)`;
    }

    this.slides = Array.from(this.slideTrack.children);
  }

  cloneItem(item) {
    return item.cloneNode(true);
  }

  appendSlide(item, index) {
    const slide = document.createElement('div');
    slide.className = 'slide';
    slide.dataset.index = index;

    // 检查是否是链接包装的图片
    if (item.tagName === 'A') {
      const link = item.cloneNode(true);
      slide.appendChild(link);
    } else {
      const clonedItem = item.cloneNode(true);
      slide.appendChild(clonedItem);
    }

    this.slideTrack.appendChild(slide);
  }

  createPagination() {
    for (let i = 0; i < this.totalItems; i++) {
      const dot = document.createElement('div');
      dot.className = 'dot';
      dot.dataset.index = i;
      this.paginationContainer.appendChild(dot);
    }

    this.dots = Array.from(this.paginationContainer.querySelectorAll('.dot'));
  }

  setupEventListeners() {
    // 箭头按钮事件
    if (this.showArrows) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // 分页点击事件
    if (this.showPagination) {
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index);
          this.goToSlide(index);
        });
      });
    }

    // 触摸滑动事件
    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener('touchstart', (e) => {
      this.pauseAutoplay();
      touchStartX = e.changedTouches[0].screenX;
    });

    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
      if (this.autoplay) {
        this.startAutoplay();
      }
    });

    // 鼠标悬停暂停自动播放
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }

  handleSwipe(startX, endX) {
    if (this.isTransitioning) return;

    const threshold = 50;
    if (startX - endX > threshold) {
      // 向左滑动
      this.nextSlide();
    } else if (endX - startX > threshold) {
      // 向右滑动
      this.prevSlide();
    }
  }

  showSlide(index) {
    if (this.isTransitioning || !this.slideTrack) return; // 确保slideTrack存在

    this.isTransitioning = true;
    this.currentIndex = index;

    // 计算滑动距离
    let offset;
    if (this.loop) {
      offset = -(index + 1) * 100;
    } else {
      offset = -index * 100;
    }

    this.slideTrack.style.transition = `transform ${this.speed}ms ease`;
    this.slideTrack.style.transform = `translateX(${offset}%)`;

    // 更新分页指示器
    if (this.showPagination) {
      this.updatePagination();
    }

    // 确保过渡结束后重置isTransitioning状态
    const transitionEndHandler = () => {
      this.handleTransitionEnd();
      this.slideTrack.removeEventListener('transitionend', transitionEndHandler);
    };

    this.slideTrack.addEventListener('transitionend', transitionEndHandler);
  }

  handleTransitionEnd() {
    this.isTransitioning = false;

    if (!this.loop || !this.slideTrack) return; // 确保slideTrack存在

    // 处理无限循环的边界情况
    if (this.currentIndex === -1) {
      // 如果滑到最前面的克隆片，跳转到实际的最后一张
      requestAnimationFrame(() => {
        this.slideTrack.style.transition = 'none';
        this.currentIndex = this.totalItems - 1;
        this.slideTrack.style.transform = `translateX(-${(this.totalItems) * 100}%)`;
      });
    } else if (this.currentIndex === this.totalItems) {
      // 如果滑到最后面的克隆片，跳转到实际的第一张
      requestAnimationFrame(() => {
        this.currentIndex = 0;
        this.slideTrack.style.transition = 'none';
        this.slideTrack.style.transform = `translateX(-100%)`;
      });
    }
  }

  updatePagination() {
    // 计算实际索引（考虑循环）
    const actualIndex = (this.currentIndex + this.totalItems) % this.totalItems;

    this.dots.forEach((dot, i) => {
      if (i === actualIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  goToSlide(index) {
    if (this.loop) {
      this.showSlide(index);
    } else {
      if (index >= 0 && index < this.totalItems) {
        this.showSlide(index);
      }
    }
  }

  nextSlide() {
    if (this.loop || this.currentIndex < this.totalItems - 1) {
      this.showSlide(this.currentIndex + 1);
    }
  }

  prevSlide() {
    if (this.loop || this.currentIndex > 0) {
      this.showSlide(this.currentIndex - 1);
    }
  }

  startAutoplay() {
    this.pauseAutoplay();
    if (this.totalItems <= 1) return; // 单张图片不需要自动播放

    this.autoplayInterval = setInterval(() => {
      if (this.slideTrack) { // 确保slideTrack存在
        this.nextSlide();
      }
    }, this.duration);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  disconnectedCallback() {
    // 清理资源
    this.pauseAutoplay();

    // 移除事件监听器
    if (this.showArrows && this.prevButton && this.nextButton) {
      this.prevButton.removeEventListener('click', this.prevSlide);
      this.nextButton.removeEventListener('click', this.nextSlide);
    }

    if (this.slideTrack) {
      this.slideTrack.removeEventListener('transitionend', this.handleTransitionEnd);
    }
  }

  static get observedAttributes() {
    return ['autoplay', 'loop', 'initial-index', 'speed', 'duration', 'arrows', 'pagination', 'aspect-ratio'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // 处理属性变化
    if (oldValue === newValue) return;

    switch (name) {
      case 'autoplay':
        this.autoplay = this.hasAttribute('autoplay');
        if (this.autoplay) {
          this.startAutoplay();
        } else {
          this.pauseAutoplay();
        }
        break;
      case 'loop':
        this.loop = this.hasAttribute('loop');
        // 重新渲染需要重新连接组件
        break;
      case 'initial-index':
        this.initialIndex = parseInt(newValue || '0');
        break;
      case 'speed':
        this.speed = parseInt(newValue || '500');
        if (this.slideTrack) {
          this.slideTrack.style.transitionDuration = `${this.speed}ms`;
        }
        break;
      case 'duration':
        this.duration = parseInt(newValue || '3000');
        if (this.autoplay) {
          this.startAutoplay();
        }
        break;
      case 'arrows':
        this.showArrows = this.hasAttribute('arrows');
        // 重新渲染需要重新连接组件
        break;
      case 'pagination':
        this.showPagination = this.hasAttribute('pagination');
        // 重新渲染需要重新连接组件
        break;
      case 'aspect-ratio':
        this.aspectRatio = newValue;
        // 需要更新容器样式
        const container = this.shadowRoot.querySelector('.slider-container') ||
          this.shadowRoot.querySelector('.single-item');
        if (container) {
          if (this.aspectRatio) {
            container.style.aspectRatio = this.aspectRatio;
          } else {
            container.style.aspectRatio = '';
          }
        }
        break;
    }
  }
}


customElements.define('carousel-image', CarouselImage); 